import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { faturasService, type FiltroCandidatas } from "../services/faturasService";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/lib/apiClient";
import type {
  GerarFaturasRequest, BaixarFaturaRequest, FiltroFaturas,
} from "../types";

/**
 * Gerar/baixar/estornar fatura mexe no que a tela de Contas a Receber mostra
 * (as parcelas ficam marcadas como faturadas, ou voltam a ser cobráveis), então
 * toda mutação invalida também as chaves de contas a receber. Sem isso a outra
 * tela continua exibindo o estado anterior e o usuário fatura duas vezes.
 */
function useInvalidarFinanceiro() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ["faturas"] });
    queryClient.invalidateQueries({ queryKey: ["faturas-candidatas"] });
    queryClient.invalidateQueries({ queryKey: ["contas-receber"] });
    queryClient.invalidateQueries({ queryKey: ["contas-receber-parcelas"] });
    queryClient.invalidateQueries({ queryKey: ["contas-receber-gerencial-clientes"] });
    queryClient.invalidateQueries({ queryKey: ["contas-receber-gerencial-titulos"] });
  };
}

/** Parcelas faturáveis, agrupadas por cliente. */
export const useCandidatasFatura = (filtro: FiltroCandidatas = {}) => {
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["faturas-candidatas", filtro],
    queryFn: () => faturasService.getCandidatas(filtro),
    placeholderData: (prev) => prev,
  });

  return { clientes: data ?? [], isLoading, isFetching };
};

/** Listagem das faturas geradas. */
export const useFaturas = (filtro: FiltroFaturas = {}) => {
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["faturas", filtro],
    queryFn: () => faturasService.getAll(filtro),
    placeholderData: (prev) => prev,
  });

  return {
    faturas: data?.itens ?? [],
    total: data?.total ?? 0,
    totalPaginas: data?.totalPaginas ?? 0,
    isLoading,
    isFetching,
  };
};

/** Fatura + as parcelas que ela agrupa. */
export const useFaturaDetalhe = (id: number | null) => {
  const { data, isLoading } = useQuery({
    queryKey: ["faturas", "detalhe", id],
    queryFn: () => faturasService.getById(id!),
    enabled: id !== null,
  });

  return { detalhe: data, isLoading };
};

export const useFaturasMutations = () => {
  const invalidar = useInvalidarFinanceiro();

  const gerar = useMutation({
    mutationFn: (req: GerarFaturasRequest) => faturasService.gerar(req),
    onSuccess: (r) => {
      invalidar();
      const total = r.valorTotal.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
      toast.success(
        r.qtdFaturas === 1
          ? `Fatura gerada — ${total}`
          : `${r.qtdFaturas} faturas geradas — ${total}`,
      );
      // Avisos não são erro: parcela já faturada/paga que veio no request é
      // ignorada de propósito. Mostrar é melhor do que sumir em silêncio.
      r.avisos?.forEach((a) => toast.warning(a));
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const baixar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: BaixarFaturaRequest }) =>
      faturasService.baixar(id, req),
    onSuccess: (d) => {
      invalidar();
      toast.success(`Fatura recebida — ${d.titulos.length} título(s) quitado(s)`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const estornar = useMutation({
    mutationFn: (id: number) => faturasService.estornar(id),
    onSuccess: () => {
      invalidar();
      toast.success("Fatura estornada — os títulos voltaram ao contas a receber");
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const pdf = useMutation({
    mutationFn: (id: number) => faturasService.baixarPdf(id),
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return {
    gerarFaturas: gerar.mutate,
    isGerando: gerar.isPending,
    baixarFatura: baixar.mutate,
    isBaixando: baixar.isPending,
    estornarFatura: estornar.mutate,
    isEstornando: estornar.isPending,
    baixarPdf: pdf.mutate,
    isBaixandoPdf: pdf.isPending,
  };
};
